import { TIER_INDEX } from './countryConfig';
import { supabase } from './supabase';

// Shared helpers for branched classes (Nigeria).
//
// A grade with branches/streams is a group of class rows sharing one
// grade_group_id. Once a group has 2+ members, its representative (lowest
// sort_order member, always the original class) is no longer a real teaching
// class — it becomes the management screen where students are sorted into
// branches. Everything that COUNTS classes (dashboard card, Pulse alerts,
// timetable checks) must skip those representatives, so this is the single
// place that rule lives. When a group drops back to one member, that member is
// a normal class again and is counted again automatically.
//
// Classes with no grade_group_id (every Pakistani class and every unbranched
// Nigerian class) are never management-only, so these helpers are no-ops there.

export interface GroupableClass {
  id: string;
  grade_group_id?: string | null;
  sort_order?: number | null;
}

export function getManagementOnlyClassIds(classes: GroupableClass[]): Set<string> {
  const byGroup = new Map<string, GroupableClass[]>();
  classes.forEach(c => {
    if (!c.grade_group_id) return;
    const arr = byGroup.get(c.grade_group_id) || [];
    arr.push(c);
    byGroup.set(c.grade_group_id, arr);
  });
  const ids = new Set<string>();
  byGroup.forEach(members => {
    if (members.length <= 1) return;
    const representative = members.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))[0];
    ids.add(representative.id);
  });
  return ids;
}

// Only real teaching classes: branched groups' management representatives removed.
export function teachingClasses<T extends GroupableClass>(classes: T[]): T[] {
  const managementIds = getManagementOnlyClassIds(classes);
  return classes.filter(c => !managementIds.has(c.id));
}

// "Science-class behaviour" (streams, Core/Shared subjects, per-subject
// rankings with no overall composite rank) belongs to SS-style classes:
//  • the real SS classes — highschool tier, position 4+ (SS1-3 / Grade 10-12), and
//  • custom classes in a Nigerian school — freeform classes the school added
//    itself, which carry no tier.
// Tier-less classes in a Pakistani school are ordinary classes, so country is
// required to tell the two apart. Pass school.country.
export function isSecondaryStyleClass(
  c: { tier?: number | null; grade_level?: number | null } | null | undefined,
  country: string | null | undefined,
): boolean {
  if (!c) return false;
  if (c.tier === TIER_INDEX.highschool && (c.grade_level ?? 0) >= 4) return true;
  return country === 'nigeria' && (c.tier === null || c.tier === undefined);
}

// Same rule, for screens that only have ids (parent / student portals): looks
// up the class's tier + position and the school's country, then applies it.
export async function isSecondaryStyleClassById(classId: string, schoolId: string): Promise<boolean> {
  const [{ data: cls }, { data: sch }] = await Promise.all([
    supabase.from('classes').select('tier, grade_level').eq('id', classId).maybeSingle(),
    supabase.from('schools').select('country').eq('id', schoolId).maybeSingle(),
  ]);
  return isSecondaryStyleClass(cls, sch?.country);
}
